import { useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";

const CAMPAIGN_TYPES = [
  { value: "promotional", label: "Promotional" },
  { value: "newsletter", label: "Newsletter" },
  { value: "announcement", label: "Announcement" },
  { value: "onboarding", label: "Onboarding" },
];

const TARGET_AUDIENCES = [
  { value: "all", label: "All Subscribers" },
  { value: "new", label: "New Customers" },
  { value: "active", label: "Active Users" },
  { value: "inactive", label: "Inactive Users" },
];

const SEND_TIMES = [
  { value: "now", label: "Send Immediately" },
  { value: "schedule", label: "Schedule for Later" },
  { value: "recurring", label: "Recurring Campaign" },
];

export function EmailCampaignModal({ open, onClose, onSubmit, toast }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [sendTime, setSendTime] = useState("");
  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [content, setContent] = useState("");
  const [trackOpens, setTrackOpens] = useState(true);
  const [trackClicks, setTrackClicks] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast?.({ title: "Missing name", description: "Please enter a campaign name." });
      return;
    }
    onSubmit?.({
      name: name.trim(),
      description,
      type,
      targetAudience,
      sendTime,
      subject,
      preheader,
      content,
      trackOpens,
      trackClicks,
    });
    toast?.({ title: "Campaign created", description: `"${name.trim()}" has been created.` });
    resetForm();
    onClose?.();
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setType("");
    setTargetAudience("");
    setSendTime("");
    setSubject("");
    setPreheader("");
    setContent("");
    setTrackOpens(true);
    setTrackClicks(true);
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Create Email Campaign" maxWidth="640px">
      <p style={{ margin: "0 0 20px", fontSize: 14, color: "#6b7280" }}>
        Create and manage email campaigns with customizable templates.
      </p>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Campaign name</label>
          <Input placeholder="Enter campaign name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Description</label>
          <Textarea placeholder="Brief description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Campaign type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}
          >
            <option value="">Select type</option>
            {CAMPAIGN_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Target audience</label>
          <select
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}
          >
            <option value="">Select audience</option>
            {TARGET_AUDIENCES.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Send time</label>
          <select
            value={sendTime}
            onChange={(e) => setSendTime(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}
          >
            <option value="">Select timing</option>
            {SEND_TIMES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Email subject</label>
          <Input placeholder="Subject line" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Preheader</label>
          <Input placeholder="Preview text" value={preheader} onChange={(e) => setPreheader(e.target.value)} />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 14, fontWeight: 500 }}>Email content</label>
          <Textarea placeholder="Write your email content..." value={content} onChange={(e) => setContent(e.target.value)} rows={4} style={{ minHeight: 100 }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={trackOpens} onChange={(e) => setTrackOpens(e.target.checked)} />
            <span style={{ fontSize: 14 }}>Track opens</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={trackClicks} onChange={(e) => setTrackClicks(e.target.checked)} />
            <span style={{ fontSize: 14 }}>Track clicks</span>
          </label>
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 8 }}>
          <Button type="button" $variant="outline" onClick={handleClose}>Cancel</Button>
          <Button type="submit">Create Campaign</Button>
        </div>
      </form>
    </Modal>
  );
}
